import {getColorStyle} from '@/utilities/color-style-map';
import classNames from 'classnames';
import {ComponentChildren} from 'preact';

export type PlanSelectorButtonProps = {
  bottom?: ComponentChildren;
  color?: string;
  dataTestId?: string;
  isSelected?: boolean;
  middle?: ComponentChildren;
  onClick?: () => void;
  top?: ComponentChildren;
};

/**
 * Renders a button for selecting a plan.
 * The button displays text at the top, middle, and bottom.
 * The button's color can be customized.
 * The button can be selected or deselected.
 * The button can be clicked to trigger an action.
 *
 * @param bottom - The text to display at the bottom of the button.
 * @param color - The color of the button.
 * @param dataTestId - The data-testid attribute for testing.
 * @param isSelected - Indicates whether the button is selected.
 * @param middle - The text to display in the middle of the button.
 * @param onClick - The function to call when the button is clicked.
 * @param top - The text to display at the top of the button.
 * @returns The rendered button component.
 */
export default function PlanSelectorButton({
  bottom,
  color = '',
  dataTestId = '',
  isSelected,
  middle,
  onClick,
  top,
}: PlanSelectorButtonProps) {
  const colorStyle = getColorStyle(color);

  return (
    <button
      data-testId={dataTestId}
      onClick={onClick}
      className={classNames('flex flex-col items-center rounded-xl p-2', {
        [`border-2 ${colorStyle?.border} ${colorStyle?.backgroundOpacity12}`]:
          isSelected,
        'border border-deep-ocean-blue/70': !isSelected,
      })}
    >
      {!!top && (
        <span className="text-2xs leading-normal text-deep-ocean-blue/70">
          {top}
        </span>
      )}
      {!!middle && (
        <span className="font-left21 text-2.5xl font-medium">{middle}</span>
      )}
      {!!bottom && <span className="text-sm">{bottom}</span>}
    </button>
  );
}
